/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Apple, TrendingUp, Check, Menu, X, ArrowRight, Mail, Phone, MapPin, Send, Target, Flame, Lock, ShieldCheck, CreditCard, Inbox, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { mockFeatures, mockSteps, mockPricing, mockAppFeatures } from '../mock';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import '../styles/CheckoutModal.css';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Dumbbell,
  Apple,
  TrendingUp
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCheckoutInfo, setShowCheckoutInfo] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const { isAuthenticated, user } = useAuth();

  // Rotating motivational quotes shown next to the logo
  const motivationalQuotes = [
    "The first step was the hardest. You're already on your way.",
    "Discipline is choosing what you want most over what you want now.",
    "Small steps every day. That's how peaks are reached.",
    "Your future self will thank you for showing up today."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % motivationalQuotes.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [motivationalQuotes.length]);

  // Lock body scroll when checkout modal is open (important for iOS PWA webview)
  useEffect(() => {
    if (showCheckoutInfo) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showCheckoutInfo]);

  // All mockup images for the carousel
  const mockupImages = [
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/yt17fd82_IMG_9437.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/hegv90a2_IMG_9442.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/np8pagv4_IMG_9444.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/9lz1ydr3_IMG_9445.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/kptvx3vd_IMG_9446.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/ig8kccpe_IMG_9447.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/r63lo8h7_IMG_9448.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/middk6ib_IMG_9449.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/v80a1l4u_IMG_9450.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/6nm5jxb4_IMG_9451.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/5rxvks0k_IMG_9452.png",
    "https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/p8ot06ev_image.png"
  ];

  // Detect device and get app store link
  const getAppStoreLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'https://apps.apple.com/app/athlopeak'; // Replace with your actual App Store link
    }
    
    // Android detection
    if (/android/i.test(userAgent)) {
      return 'https://play.google.com/store/apps/details?id=com.athlopeak'; // Replace with your actual Play Store link
    }
    
    // Default to App Store for desktop/unknown
    return 'https://apps.apple.com/app/athlopeak';
  };

  const handleViewApp = () => {
    window.open(getAppStoreLink(), '_blank');
  };

  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -80px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fadeIn');
        }
      });
    }, observerOptions);

    // Observe all animated elements
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));

    // Advanced scroll reveal for section headers
    const headerObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, index * 100);
        }
      });
    }, { threshold: 0.2 });

    document.querySelectorAll('.section-header').forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
      headerObserver.observe(el);
    });

    return () => {
      observer.disconnect();
      headerObserver.disconnect();
    };
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email
      });
      
      toast.success('Thanks for subscribing! Check your inbox.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact/submit`, contactForm);
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePricingClick = async (plan) => {
    // Show checkout info modal FIRST
    setSelectedPlan(plan);
    setShowCheckoutInfo(true);
  };

  const proceedToCheckout = async () => {
    if (!selectedPlan) return;
    
    const userEmail = user?.email || email || '';
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      setShowCheckoutInfo(false);
      toast.info(`Starting secure checkout for ${selectedPlan.name}...`);
      
      // Create checkout session
      const response = await axios.post(`${API}/api/checkout/create`, {
        plan_type: selectedPlan.name.toLowerCase().replace(/\s/g, ''),
        email: userEmail
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Could not start checkout. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        console.error('Checkout error:', error);
      }
    }
  };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="network-header">
        <div className="nav-wrapper">
          <Link to="/" className="network-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/hkt7j1uz_Ontwerp%20zonder%20titel%20%282%29.png" 
              alt="AthloPeak"
              className="logo-img"
            />
          </Link>

          {/* Motivational Quote — rotating, placed in the empty space next to the logo */}
          <div className="nav-quote" data-testid="nav-quote">
            <span className="nav-quote-mark" aria-hidden="true">&ldquo;</span>
            <p
              key={quoteIndex}
              className="nav-quote-text"
              data-testid="nav-quote-text"
            >
              {motivationalQuotes[quoteIndex]}
            </p>
            <span className="nav-quote-mark nav-quote-mark-end" aria-hidden="true">&rdquo;</span>
          </div>

          <div className="network-nav desktop-nav">
            <a href="#features" className="network-nav-link">Features</a>
            <a href="#how-it-works" className="network-nav-link">How It Works</a>
            <a href="#pricing" className="network-nav-link">Pricing</a>
            <a href="#contact" className="network-nav-link">Contact</a>
            <a
              href="https://apps.apple.com/nl/app/athlopeak/id6751170384"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="btn-cta-small" data-testid="nav-download">Get the app</Button>
            </a>
          </div>

          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <a href="#features" className="network-nav-link" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#how-it-works" className="network-nav-link" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#pricing" className="network-nav-link" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#contact" className="network-nav-link" onClick={() => setMobileMenuOpen(false)}>Contact</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="aurora-bg"></div>
        <div className="hero-content container">
          <div className="hero-text animated">
            <h1 className="display-large hero-title">
              Your Smart Personal Trainer
            </h1>
            
            <p className="body-large hero-subtitle">
              AthloPeak builds your personal training and nutrition plan in seconds. Workouts that adapt as you improve, meals you'll actually eat, and a smart coach that keeps you on track — every single day.
            </p>

            {/* Primary hero CTA — sends visitors into the free trial funnel */}
            <div className="hero-primary-cta" data-testid="hero-primary-cta">
              <Link to="/probeer-gratis" className="hero-trial-cta" data-testid="hero-try-free">
                Try for free
                <ArrowRight size={20} />
              </Link>
              <span className="hero-trial-cta-sub">No account needed to start</span>
            </div>
            
            <div className="hero-trial-message" style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px'
            }}>
              <p style={{ margin: 0, color: '#10b981', fontWeight: 600 }}>
                14-day trial ended? Continue where you left off and unlock everything.
              </p>
            </div>

            {/* CTA group: existing customer */}
            <div className="cta-block existing-user-block" data-testid="cta-existing">
              <span className="cta-eyebrow">EXISTING CUSTOMER</span>
              <h3 className="cta-block-title">Trial ended? Unlock premium</h3>
              <p className="cta-block-sub">
                You already have an AthloPeak account. Continue your plan and keep all your progress.
              </p>
              <div className="hero-cta-group">
                <Button className="btn-cta" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })} data-testid="hero-unlock-btn">
                  Unlock Full Access
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </Button>
              </div>
            </div>

            {/* CTA group: new customer */}
            <div className="cta-block new-user-block" data-testid="cta-new">
              <span className="cta-eyebrow">NEW HERE?</span>
              <h3 className="cta-block-title">Start with a 14-day free trial</h3>
              <p className="cta-block-sub">
                Download the app and try AthloPeak free for 14 days. No credit card required.
              </p>
              <div className="store-buttons">
                <a 
                  href="https://apps.apple.com/nl/app/athlopeak/id6751170384" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="store-button app-store"
                  data-testid="hero-app-store"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="store-icon">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="store-button-text">
                    <span className="store-button-label">Download on the</span>
                    <span className="store-button-name">App Store</span>
                  </div>
                </a>
                
                <a 
                  href="https://play.google.com/store/apps/details?id=com.athlopeak" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="store-button google-play"
                  data-testid="hero-google-play"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="store-icon">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="store-button-text">
                    <span className="store-button-label">GET IT ON</span>
                    <span className="store-button-name">Google Play</span>
                  </div>
                </a>
              </div>
            </div>
            
            <div className="hero-trust-badges">
              <div className="trust-badge">
                <Check size={16} className="trust-check" />
                <span>No credit card required</span>
              </div>
              <div className="trust-badge">
                <Check size={16} className="trust-check" />
                <span>Cancel anytime</span>
              </div>
              <div className="trust-badge">
                <Check size={16} className="trust-check" />
                <span>iOS & Android</span>
              </div>
            </div>
          </div>

          {/* Hero iPhone Mockup */}
          <div className="hero-mockup animated">
            <div className="hero-phone-container">
              <div className="hero-phone-bezel">
                <div className="hero-phone-notch"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/pjwfm1rm_IMG_9444.png"
                  alt="AthloPeak App"
                  className="hero-phone-screen"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section - Swipeable Cards */}
      <section className="offer-section">
        <div className="container">
          <div className="offer-header animated">
            <h2 className="display-medium">See What We Offer</h2>
            <p className="body-large">Swipe to explore everything AthloPeak brings to your fitness journey</p>
          </div>

          <div className="offer-cards-wrapper">
            <div className="offer-cards-scroll">
              {/* Card 1: How It Works */}
              <div className="offer-card animated">
                <div className="offer-card-number">01</div>
                <h3 className="heading-2">Here's How We Do It</h3>
                <p className="body-medium">
                  Tell us what you're after. Weight loss? Building muscle? Training for a race? 
                  What do you like doing — lifting, running, cycling, or just bodyweight stuff? 
                  How fit are you right now? When can you actually train?
                </p>
                <p className="body-medium offer-card-highlight">
                  We build your entire program in seconds.
                </p>
              </div>

              {/* Card 2: Training */}
              <div className="offer-card animated">
                <div className="offer-card-number">02</div>
                <h3 className="heading-2">Training Built For You</h3>
                <div className="offer-card-list">
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Workouts that match where you're at and where you want to go</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Every exercise explained like a real coach would</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Videos showing exactly how it's done</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Visual muscle maps so you know what you're working</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Programs that build over 4-week cycles and adjust when you improve</span>
                  </div>
                </div>
              </div>

              {/* Card 3: Nutrition */}
              <div className="offer-card animated">
                <div className="offer-card-number">03</div>
                <h3 className="heading-2">Eating That Makes Sense</h3>
                <div className="offer-card-list">
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Three meals plus snacks every day, matched to how hard you're training</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Actual recipes you can follow</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>We explain why you're eating what you're eating</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Hate something? Swap it instantly. One tap, done</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Vegetarian, vegan, gluten-free — we handle it</span>
                  </div>
                </div>
              </div>

              {/* Card 4: Progress */}
              <div className="offer-card animated">
                <div className="offer-card-number">04</div>
                <h3 className="heading-2">Watch Yourself Improve</h3>
                <p className="body-medium">
                  Track everything: weight, workouts, what you eat. Check off workouts and meals as you go.
                </p>
                <p className="body-medium offer-card-highlight">
                  Your program adjusts automatically based on how you're actually doing.
                </p>
              </div>

              {/* Card 5: For Everyone */}
              <div className="offer-card animated">
                <div className="offer-card-number">05</div>
                <h3 className="heading-2">Works For Everyone</h3>
                <div className="offer-card-list">
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>New to this? We start slow and build you up the right way</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Been training for years? You get real periodization</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Got a race? 5K to marathon, cycling to triathlons</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Busy schedule or limited equipment? We work around it</span>
                  </div>
                  <div className="offer-list-item">
                    <Check size={20} className="offer-check" />
                    <span>Whatever your level — beginner, intermediate or pro</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="offer-scroll-hint animated">
              <p className="caption">← Swipe to see more →</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Continue Section - For Trial Users */}
      <section className="why-continue-section" style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)'
      }}>
        <div className="container">
          <div className="section-header-center animated">
            <h2 className="display-small">Why Keep Going?</h2>
            <p className="body-large section-description">
              You've built momentum. Don't let it slip away.
            </p>
          </div>

          <div className="app-features-grid" style={{ marginTop: '48px' }}>
            <div className="app-feature-card animated">
              <div className="feature-icon-wrap"><TrendingUp size={36} strokeWidth={2} /></div>
              <h3 className="app-feature-title">Your Progress Matters</h3>
              <p className="app-feature-desc">
                Every workout, every meal logged—it's all data that helps you improve. 
                Stopping now means losing valuable insights.
              </p>
            </div>

            <div className="app-feature-card animated">
              <div className="feature-icon-wrap"><Target size={36} strokeWidth={2} /></div>
              <h3 className="app-feature-title">Consistency is Everything</h3>
              <p className="app-feature-desc">
                The hardest part is getting started. You've already done that. 
                Keep the rhythm going and make it stick.
              </p>
            </div>

            <div className="app-feature-card animated">
              <div className="feature-icon-wrap"><Flame size={36} strokeWidth={2} /></div>
              <h3 className="app-feature-title">Momentum Compounds</h3>
              <p className="app-feature-desc">
                Two weeks in, your body is adapting. Your habits are forming. 
                This is when real transformation begins.
              </p>
            </div>

            <div className="app-feature-card animated">
              <div className="feature-icon-wrap"><Dumbbell size={36} strokeWidth={2} /></div>
              <h3 className="app-feature-title">You Deserve This</h3>
              <p className="app-feature-desc">
                You started for a reason. Your goals haven't changed. 
                Keep becoming the best version of yourself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Features Grid */}
      <section className="stats-section">
        <div className="container">
          <div className="app-features-grid">
            {mockAppFeatures.map((feature, index) => (
              <div key={feature.id} className="app-feature-card animated" style={{ transitionDelay: `${index * 100}ms` }}>
                <h3 className="app-feature-title">{feature.title}</h3>
                <p className="app-feature-desc">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section">
        <div className="container">
          <div className="social-proof-content animated">
            <div className="mockups-showcase">
              <div className="mockups-scroll">
                {mockupImages.concat(mockupImages).map((img, index) => (
                  <div key={`mockup-${index}-${img.split('/').pop()}`} className="phone-mockup">
                    <div className="phone-frame">
                      <div className="phone-notch"></div>
                      <img 
                        src={img}
                        alt={`AthloPeak App Screenshot ${(index % mockupImages.length) + 1}`}
                        className="phone-screenshot"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="social-proof-text">
              <h2 className="heading-1">Stop Guessing. Start Training Smart.</h2>
              <p className="body-large">
                Most people fail because they're following random workouts that don't match their goals. 
                AthloPeak gives you a real plan that adjusts based on how you're actually doing.
              </p>
              <p className="body-large" style={{ marginTop: 'var(--spacing-medium)' }}>
                Your nutrition syncs with your training intensity. Heavy lifting day? You get more carbs. 
                Rest day? Meals adjust automatically. Everything works together.
              </p>
              <p className="body-large" style={{ marginTop: 'var(--spacing-medium)' }}>
                A smart coach analyzes your progress every week. Getting stronger faster? Your program levels up. 
                Struggling? It adapts to keep you moving forward. No guessing, just results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="display-medium">What You Get</h2>
            <p className="body-large">Everything you need. Nothing you don't.</p>
          </div>
          
          <div className="network-grid">
            {mockFeatures.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="network-card feature-card animated" style={{ transitionDelay: `${index * 150}ms` }}>
                  <CardHeader>
                    <div className="feature-icon-wrapper">
                      <IconComponent size={32} className="feature-icon" />
                    </div>
                    <CardTitle className="network-card-title">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="network-card-content">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="display-medium">How It Works</h2>
            <p className="body-large">Three steps. That's it.</p>
          </div>

          <div className="steps-container">
            {mockSteps.map((step, index) => (
              <div key={step.id} className="step-card animated" style={{ transitionDelay: `${index * 200}ms` }}>
                <div className="step-number">{step.step}</div>
                <h3 className="heading-2">{step.title}</h3>
                <p className="body-medium">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="display-medium">Continue Your Progress</h2>
            <p className="body-large">One subscription. Full access. Pick what works for you.</p>
          </div>

          <div className="pricing-grid">
            {mockPricing.map((plan, index) => (
              <Card 
                key={plan.id} 
                className={`pricing-card animated ${plan.popular ? 'popular' : ''}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {plan.discount && <div className="discount-badge">{plan.discount}</div>}
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                
                <CardHeader>
                  <CardTitle className="heading-2">{plan.name}</CardTitle>
                  <div className="pricing-amount">
                    <span className="price-euro">€</span>
                    <span className="price-value">{plan.price}</span>
                    <span className="price-interval">/{plan.interval}</span>
                  </div>
                  {plan.totalPrice !== plan.price && (
                    <p className="pricing-total">€{plan.totalPrice} billed {plan.name === '6 Months' ? 'every 6 months' : 'annually'}</p>
                  )}
                </CardHeader>

                <CardContent>
                  <ul className="pricing-features">
                    {plan.features.map((feature, idx) => (
                      <li key={`${plan.name}-feature-${idx}-${feature.substring(0, 20)}`} className="pricing-feature-item">
                        <Check size={20} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button 
                    className={plan.popular ? "btn-cta full-width" : "btn-primary full-width"}
                    onClick={() => handlePricingClick(plan)}
                  >
                    Continue Your Plan
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <div className="container">
          <Card className="newsletter-card animated">
            <CardHeader>
              <CardTitle className="heading-1">Get Updates</CardTitle>
              <CardDescription className="body-large">
                Training tips and new features. No spam.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                <Input 
                  type="email" 
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="newsletter-input"
                />
                <Button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="display-medium">Questions?</h2>
            <p className="body-large">We're here to help</p>
          </div>

          <div className="contact-grid">
            <Card className="contact-form-card animated">
              <CardHeader>
                <CardTitle className="heading-2">Send us a message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="contact-form">
                  <div className="form-group">
                    <Input 
                      type="text" 
                      placeholder="Your Name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <Input 
                      type="email" 
                      placeholder="Your Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <Textarea 
                      placeholder="Your Message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      rows={5}
                    />
                  </div>
                  <Button type="submit" className="btn-primary full-width" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Message
                        <Send size={18} style={{ marginLeft: '8px' }} />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="contact-info animated" style={{ transitionDelay: '200ms' }}>
              <Card className="contact-info-card">
                <CardContent>
                  <div className="contact-info-item">
                    <Mail size={24} />
                    <div>
                      <p className="contact-label caption">Email</p>
                      <a href="mailto:info@athlopeak.com" className="contact-value body-medium">info@athlopeak.com</a>
                    </div>
                  </div>
                  <div className="contact-info-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <div>
                      <p className="contact-label caption">Instagram</p>
                      <a href="https://instagram.com/athlopeak" target="_blank" rel="noopener noreferrer" className="contact-value body-medium">@athlopeak</a>
                    </div>
                  </div>
                  <div className="contact-info-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <div>
                      <p className="contact-label caption">TikTok</p>
                      <a href="https://tiktok.com/@athlopeak" target="_blank" rel="noopener noreferrer" className="contact-value body-medium">@athlopeak</a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="aurora-bg-alt"></div>
        <div className="container">
          <div className="final-cta-content animated">
            <h2 className="display-large">Ready to Start?</h2>
            <p className="body-large">Get access to everything today</p>
            <Button className="btn-cta btn-cta-large" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>
              Get Started Now
              <ArrowRight size={24} style={{ marginLeft: '12px' }} />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_peak-coaching-hub-1/artifacts/hkt7j1uz_Ontwerp%20zonder%20titel%20%282%29.png" 
                  alt="AthloPeak"
                  className="footer-logo-img"
                />
              </div>
              <p className="footer-tagline body-small">
                Training that actually works
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <a href="#features" className="footer-link">Features</a>
                <a href="#how-it-works" className="footer-link">How It Works</a>
                <a href="#pricing" className="footer-link">Pricing</a>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Connect</h4>
                <a href="mailto:info@athlopeak.com" className="footer-link">info@athlopeak.com</a>
                <a href="https://instagram.com/athlopeak" target="_blank" rel="noopener noreferrer" className="footer-link">Instagram</a>
                <a href="https://tiktok.com/@athlopeak" target="_blank" rel="noopener noreferrer" className="footer-link">TikTok</a>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <a href="#" className="footer-link">Privacy</a>
                <a href="#" className="footer-link">Terms</a>
                <a href="#" className="footer-link">Cookie Policy</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="caption">© 2025 AthloPeak. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Checkout Info Modal */}
      {showCheckoutInfo && (
        <div className="modal-overlay" onClick={() => setShowCheckoutInfo(false)} data-testid="checkout-modal-overlay">
          <div className="modal-content checkout-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Lock size={20} className="modal-h2-icon" /> Secure payment via Stripe</h2>
              <button className="modal-close" onClick={() => setShowCheckoutInfo(false)} data-testid="checkout-modal-close">×</button>
            </div>
            
            <div className="modal-body">
              <div className="checkout-info-section">
                <h3><BadgeCheck size={18} className="modal-h3-icon" /> What happens next?</h3>
                <p>
                  You'll be redirected to our secure payment page (powered by Stripe).
                  You enter your payment details there safely.
                </p>
              </div>

              <div className="checkout-info-section">
                <h3><Mail size={18} className="modal-h3-icon" /> Why do we ask for your email?</h3>
                <ul>
                  <li><strong>Account linking:</strong> We automatically link your payment to your AthloPeak account</li>
                  <li><strong>Payment confirmation:</strong> You'll receive an invoice and confirmation by email</li>
                  <li><strong>Instant access:</strong> After payment we activate your premium features immediately</li>
                </ul>
              </div>

              <div className="checkout-info-section">
                <h3><ShieldCheck size={18} className="modal-h3-icon" /> Is it safe?</h3>
                <p>
                  <strong>Absolutely.</strong> We use Stripe, the most trusted payment processor in the world.
                  Your payment details are never stored on our side. Stripe is PCI-DSS Level 1 certified
                  (the highest security level).
                </p>
              </div>

              <div className="checkout-info-section">
                <h3><CreditCard size={18} className="modal-h3-icon" /> Payment methods</h3>
                <p>
                  You can pay by credit card, debit card, or iDEAL (for Dutch customers).
                </p>
              </div>

              {!user?.email && (
                <div className="checkout-info-section">
                  <h3><Inbox size={18} className="modal-h3-icon" /> Enter your email</h3>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="checkout-email-input"
                  />
                </div>
              )}

              <div className="checkout-info-highlight">
                <p>
                  <strong>You'll be redirected to the payment page right away.</strong><br/>
                  Once payment succeeds you'll get instant access to all premium features.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <Button 
                variant="outline" 
                onClick={() => setShowCheckoutInfo(false)}
                className="modal-button-secondary"
                data-testid="checkout-modal-cancel"
              >
                Cancel
              </Button>
              <Button 
                onClick={proceedToCheckout}
                className="modal-button-primary"
                data-testid="checkout-modal-proceed"
              >
                Go to secure checkout →
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
